import { presentationGenerationApi } from "@/shared/services/presentation-generation/presentation-generation.api";
import type { RegenerationOption } from "@/shared/services/presentation-generation/types";
import axios from "axios";
import {
  type ImportOption,
  type OptionRef,
  optionsFromPartialEntry,
  optionsFromResult,
} from "../stores/file-to-storydoc-generation.store";
import { normalizeOptionSlide } from "./normalize-option-slide";

export const regenerationOptions = (
  options: RegenerationOption[] | null | undefined,
): ImportOption[] | null => {
  const out = (options ?? [])
    .filter((o) => o && o.slide != null)
    .map(({ k, title, slide }) => ({
      k,
      title,
      slide: normalizeOptionSlide(slide),
    }));
  return out.length ? out : null;
};

type FetchOptionSetArgs = {
  jobId: string;
  pageNumber: number;
  rowIndex: number;
  setRef: OptionRef;
  signal?: AbortSignal;
};

// Every set a row has shown lives in the backend for the job's TTL (spec §4.3);
// undo and resume re-fetch it instead of keeping slides in browser storage.
export const fetchOptionSet = async ({
  jobId,
  pageNumber,
  rowIndex,
  setRef,
  signal,
}: FetchOptionSetArgs): Promise<ImportOption[]> => {
  if (setRef.kind === "regeneration") {
    const st = await presentationGenerationApi.getRegeneration(
      jobId,
      pageNumber,
      setRef.regenerationId,
      signal,
    );
    const options =
      st.status === "done" ? regenerationOptions(st.options) : null;
    if (!options) throw new Error("Regenerated set unavailable");
    return options;
  }
  try {
    const res = await presentationGenerationApi.getJobResult(jobId, signal);
    const options = res.result ? optionsFromResult(res.result, rowIndex) : null;
    if (!options) throw new Error("Original set unavailable");
    return options;
  } catch (e) {
    if (!axios.isAxiosError(e) || e.response?.status !== 409) throw e;
  }
  const partial = await presentationGenerationApi.getJobPartial(jobId, signal);
  const options = optionsFromPartialEntry(partial.slides[String(pageNumber)]);
  if (!options) throw new Error("Original set unavailable");
  return options;
};
